/**
 * Messages Slice
 *
 * Manages chat messages state including:
 * - Messages list
 * - Streaming state
 * - Loading and error states
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { MessageType, AssistantMessage } from '@contracts';

// ============================================
// State Types
// ============================================

export interface MessagesState {
  messages: MessageType[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  currentStreamingMessageId: string | null;
  hasMore: boolean;
  nextCursor: string | null;
  skipHistoryLoad: boolean;
  /** Array of thinking messages being displayed (for streaming progress) */
  thinkingMessages: string[];
  /** Queued message to send after ChatPage mounts (set by onboarding flow) */
  pendingQuery: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialMessagesState: MessagesState = {
  messages: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  currentStreamingMessageId: null,
  hasMore: false,
  nextCursor: null,
  skipHistoryLoad: false,
  thinkingMessages: [],
  pendingQuery: null,
};

// ============================================
// Helper Functions
// ============================================

/**
 * Generate unique ID for messages
 */
function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// Slice
// ============================================

const messagesSlice = createSlice({
  name: 'chat/messages',
  initialState: initialMessagesState,
  reducers: {
    /**
     * Add a user message and placeholder assistant message
     */
    addUserMessage: (state, action: PayloadAction<string>) => {
      const userMessage: MessageType = {
        id: generateId(),
        role: 'user',
        content: action.payload,
        timestamp: new Date().toISOString(),
      };

      const placeholderAssistant: AssistantMessage = {
        id: generateId(),
        role: 'assistant',
        content: [],
        timestamp: new Date().toISOString(),
      };

      state.messages.push(userMessage, placeholderAssistant);
      state.currentStreamingMessageId = placeholderAssistant.id;
      state.isLoading = true;
      state.error = null;
      // Clear any leftover thinking messages from previous aborted request
      state.thinkingMessages = [];
    },

    /**
     * Update streaming message content
     */
    updateStreamingMessage: (state, action: PayloadAction<AssistantMessage>) => {
      const streamedMessage = action.payload;
      const messageIndex = state.messages.findIndex(
        (msg) => msg.id === state.currentStreamingMessageId,
      );

      if (messageIndex !== -1) {
        const existingMessage = state.messages[messageIndex];
        if (existingMessage?.role === 'assistant') {
          state.messages[messageIndex] = {
            ...existingMessage,
            content: streamedMessage.content ?? [],
          };
        }
      }
    },

    /**
     * Append a new assistant message from poll result (does not replace existing).
     */
    appendPollResultMessage: (state, action: PayloadAction<AssistantMessage>) => {
      state.messages.push(action.payload);
    },

    /**
     * Complete streaming
     */
    completeStreaming: (state) => {
      state.isLoading = false;
      state.currentStreamingMessageId = null;
      state.thinkingMessages = [];
    },

    /**
     * Abort streaming - clears loading state but keeps all messages and thinking messages
     */
    abortStreaming: (state) => {
      // Keep all messages and thinking messages intact
      state.isLoading = false;
      state.currentStreamingMessageId = null;
      // Keep thinkingMessages - don't clear them
    },

    /**
     * Add a thinking message to the list (appends to array)
     */
    addThinkingMessage: (state, action: PayloadAction<string>) => {
      state.thinkingMessages.push(action.payload);
    },

    /**
     * Clear all thinking messages
     */
    clearThinkingMessages: (state) => {
      state.thinkingMessages = [];
    },

    /**
     * Set streaming error
     */
    setStreamingError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.currentStreamingMessageId = null;
      state.thinkingMessages = [];
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set messages from API response (for initial chat history load)
     */
    setMessages: (
      state,
      action: PayloadAction<{
        messages: MessageType[];
        hasMore: boolean;
        nextCursor: string | null;
      }>,
    ) => {
      state.messages = action.payload.messages;
      state.hasMore = action.payload.hasMore;
      state.nextCursor = action.payload.nextCursor;
    },

    /**
     * Prepend older messages (for pagination - loading more history)
     */
    prependMessages: (
      state,
      action: PayloadAction<{
        messages: MessageType[];
        hasMore: boolean;
        nextCursor: string | null;
      }>,
    ) => {
      // Prepend older messages to the beginning
      state.messages = [...action.payload.messages, ...state.messages];
      state.hasMore = action.payload.hasMore;
      state.nextCursor = action.payload.nextCursor;
      state.isLoadingMore = false;
    },

    /**
     * Set loading more state (for pagination)
     */
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },

    /**
     * Clear all messages
     */
    clearMessages: (state) => {
      state.messages = [];
      state.hasMore = false;
      state.nextCursor = null;
    },

    /**
     * Reset messages state
     */
    resetMessagesState: () => initialMessagesState,

    /**
     * Set flag to skip history load (used when coming from onboarding)
     */
    setSkipHistoryLoad: (state, action: PayloadAction<boolean>) => {
      state.skipHistoryLoad = action.payload;
    },

    /**
     * Queue a message to be sent after ChatPage mounts (onboarding → chat transition)
     */
    setPendingQuery: (state, action: PayloadAction<string>) => {
      state.pendingQuery = action.payload;
    },

    /**
     * Clear the pending query after it has been dispatched
     */
    clearPendingQuery: (state) => {
      state.pendingQuery = null;
    },
  },
});

// ============================================
// Actions
// ============================================

export const {
  addUserMessage,
  updateStreamingMessage,
  appendPollResultMessage,
  completeStreaming,
  abortStreaming,
  addThinkingMessage,
  clearThinkingMessages,
  setStreamingError,
  setLoading,
  setMessages,
  prependMessages,
  setLoadingMore,
  clearMessages,
  resetMessagesState,
  setSkipHistoryLoad,
  setPendingQuery,
  clearPendingQuery,
} = messagesSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const messagesReducer = messagesSlice.reducer;
